/**
 * Brand Setup → `CreateChannelInput`. PURE (no React, no fetch, no browser
 * APIs) so the whole create path is unit-testable in node.
 *
 * The contract this file exists to keep: for the same inputs, Brand Setup
 * posts the SAME channel the classic wizard would have posted. Every literal
 * below is a deliberate copy of `Onboard.tsx`'s create path (`DEFAULTS` +
 * `buildBaseLayout` + `buildLayoutPayload` + `buildLanguagePayload`), and
 * `payload.test.ts` pins the two together. If you change a number here, the
 * test must be the thing that told you to.
 */
import {
  CAPTION_UNSETTABLE,
  SPEAKER_CARD_UNSETTABLE,
  withoutUnset,
} from "../../../../src/lib/clipEditState.ts";
import type { RenderTextOverlay } from "../../../../src/remotion/types.ts";
import type { CreateChannelInput } from "../../api";
import { DEFAULT_OVERLAY } from "../overlaysEdit";
import { effectiveDesign, type BrandSetupState } from "./state";

type ChannelLayoutInput = NonNullable<CreateChannelInput["layout"]>;

/**
 * Founding layout values — a VERBATIM copy of `Onboard.tsx`'s `DEFAULTS` for
 * the fields Brand Setup does not put on a screen (it collects colour, type,
 * logo and the Studio design; everything else starts founding and is tuned in
 * the Layout step).
 */
export const LAYOUT_DEFAULTS = {
  capBottomPx: 240,
  capFontSizePx: 115,
  capTopPx: 109,
  capPunchPx: 219,
  capSidePaddingPx: 160,
  capLineHeight: 1.12,
  logoWidthPct: 36,
  logoBottomPx: 620,
  logoOpacity: 1,
  logoXPct: 50,
  speakerCardXPct: 6.0185,
  speakerCardYPct: 8.333,
  textShadow: "0 4px 12px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.85)",
} as const;

/**
 * The caption look a LEFT-TO-RIGHT brand starts on.
 *
 * The founding two-line punch layout is tuned for Arabic: a 219 px bottom line
 * rendered `nowrap`. A Latin line at its own budget (20 chars) measures past
 * the 1840 px safe area in that face and size, so it runs off the frame — and
 * it is the one look the study path deliberately refuses to infer
 * (`mapToPatch.ts`: "a Quotologies-specific look"). Latin channels get the
 * same equal-lines shape the study produces, with the last line accented.
 * Numbers are the Court & Clair golden's, minus the tighter side padding a
 * wider glyph set needs.
 */
export const LATIN_CAPTION_DEFAULTS = {
  fontSizePx: 165,
  sidePaddingPx: 140,
  lineHeight: 1.05,
  maxLines: 2,
  textShadow: "0 4px 14px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,0.6)",
} as const;

/**
 * Brand name → channel slug. Identical to `Onboard.tsx`'s slugify with ONE
 * deliberate difference: an ampersand is DROPPED rather than spelled out, so
 * "Court & Clair" reads as `court-clair` (the spec's own example) instead of
 * `court-and-clair`. Everything else — lowercase, strip, collapse, trim,
 * 40-char cap — is the same, so the two wizards agree on every other name.
 */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    // Accents are TRANSLITERATED, not dropped: without this "Léa Coaching"
    // slugged to `la-coaching` — the first machine-made identifier a French
    // brand ever sees, misspelling her name (QA 2026-09-16, M1). NFD splits
    // "é" into "e" + a combining acute, and the mark is stripped before the
    // ASCII filter below can eat the letter with it.
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9-\s]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

/** The slug that will be created: the typed one once edited, else the name's. */
export function effectiveSlug(s: BrandSetupState): string {
  return s.slugTouched && s.slug ? s.slug : slugify(s.brandName);
}

/**
 * The wizard's BASE layout, before the Layout step's Studio journal.
 * `justify: "flex-end"` pins the caption to `bottomPx` so the render matches
 * the preview — the same reason `Onboard.tsx` sets it.
 */
export function buildBaseLayout(s: BrandSetupState): ChannelLayoutInput {
  const caption: ChannelLayoutInput["caption"] =
    s.language.direction === "rtl"
      ? {
          captionStyle: "punch",
          bottomPx: LAYOUT_DEFAULTS.capBottomPx,
          fontSizePx: LAYOUT_DEFAULTS.capFontSizePx,
          twoLine: { topPx: LAYOUT_DEFAULTS.capTopPx, bottomPx: LAYOUT_DEFAULTS.capPunchPx },
          box: null,
          textShadow: LAYOUT_DEFAULTS.textShadow,
          sidePaddingPx: LAYOUT_DEFAULTS.capSidePaddingPx,
          lineHeight: LAYOUT_DEFAULTS.capLineHeight,
          letterSpacing: `${s.trackingPx}px`,
          justify: "flex-end",
        }
      : {
          captionStyle: "uniform",
          wrapMode: "auto",
          maxLines: LATIN_CAPTION_DEFAULTS.maxLines,
          accentMode: "line-punch",
          bottomPx: 0,
          fontSizePx: LATIN_CAPTION_DEFAULTS.fontSizePx,
          twoLine: null,
          box: null,
          textShadow: LATIN_CAPTION_DEFAULTS.textShadow,
          sidePaddingPx: LATIN_CAPTION_DEFAULTS.sidePaddingPx,
          lineHeight: LATIN_CAPTION_DEFAULTS.lineHeight,
          letterSpacing: `${s.trackingPx}px`,
          justify: "center",
        };
  return {
    caption,
    logo: {
      widthPct: LAYOUT_DEFAULTS.logoWidthPct,
      bottomPx: LAYOUT_DEFAULTS.logoBottomPx,
      opacity: LAYOUT_DEFAULTS.logoOpacity,
      xPct: LAYOUT_DEFAULTS.logoXPct,
    },
    speakerArrow: null,
    speakerCard: { xPct: LAYOUT_DEFAULTS.speakerCardXPct, yPct: LAYOUT_DEFAULTS.speakerCardYPct },
  };
}

/**
 * The sign-off step's PREVIEW timing.
 *
 * `DEFAULT_OVERLAY` starts nine seconds before the end and holds for five —
 * correct on a real clip, invisible on a six-second sample loop, so the one
 * screen whose whole job is "does this line look right?" would have shown
 * nothing at all. The step therefore writes `start: 0, durationSec: 6` (the
 * sample's own length) and `buildLayoutPayload` puts the real timing back on
 * the way out. Two constants, one contract, and the step imports them from
 * here so the two halves can never drift.
 */
export const SIGNOFF_PREVIEW_START = 0;
export const SIGNOFF_PREVIEW_DURATION_SEC = 6;

/**
 * Undo the preview timing above for the SIGN-OFF LINE ONLY — index 0, the slot
 * the step owns, and only when it still carries the step's exact signature.
 *
 * Both halves matter. An extra line the operator added in Adjust and timed at
 * 0 s for six seconds is their timing, not ours, and must ship as they left
 * it; and a sign-off the operator re-timed in Adjust no longer matches the
 * signature, so it ships as they left it too.
 */
function shippedOverlays(list: readonly RenderTextOverlay[]): RenderTextOverlay[] {
  return list.map((o, i) =>
    i === 0 && o.start === SIGNOFF_PREVIEW_START && o.durationSec === SIGNOFF_PREVIEW_DURATION_SEC
      ? { ...o, start: DEFAULT_OVERLAY.start, durationSec: DEFAULT_OVERLAY.durationSec }
      : o,
  );
}

/**
 * Base ⊕ the Layout step's design. Field-level, and the design always wins —
 * it is the surface the user actually saw. `withoutUnset` is load-bearing: a
 * `null` in the design is the UNSET signal (see the UNSET CONVENTION in
 * `src/lib/clipEditState.ts`), so the key is REMOVED rather than carried as a
 * literal null the server would reject.
 */
export function buildLayoutPayload(s: BrandSetupState): ChannelLayoutInput {
  const base = buildBaseLayout(s);
  // Steps ⊕ the operator's fine-tuning — the same merge the phone renders, so
  // the channel is created as the last preview showed it.
  const d = effectiveDesign(s)?.layout;
  if (!d) return base;
  return {
    caption: withoutUnset(
      { ...base.caption, ...d.caption },
      d.caption,
      CAPTION_UNSETTABLE,
    ) as ChannelLayoutInput["caption"],
    logo: { ...base.logo, ...d.logo } as ChannelLayoutInput["logo"],
    speakerArrow: base.speakerArrow ?? null,
    speakerCard: withoutUnset(
      {
        xPct: base.speakerCard?.xPct ?? LAYOUT_DEFAULTS.speakerCardXPct,
        yPct: base.speakerCard?.yPct ?? LAYOUT_DEFAULTS.speakerCardYPct,
        ...base.speakerCard,
        ...d.speakerCard,
      },
      d.speakerCard,
      SPEAKER_CARD_UNSETTABLE,
    ) as NonNullable<ChannelLayoutInput["speakerCard"]>,
    // Absent stays absent — a channel with no footage treatment renders
    // byte-identically to every channel that came before it.
    ...(d.video && Object.keys(d.video).length ? { video: d.video } : {}),
    // The Studio owns the WHOLE overlay list, so `[]` is a CLEAR, not a
    // silent no-op (same rule as applyEditState).
    ...(d.overlays && d.overlays.length ? { overlays: shippedOverlays(d.overlays) } : {}),
  };
}

/**
 * Per-role families. Display drives the punch line and the speaker-card NAME;
 * Body drives the captions and the card's role line. Both empty → omitted
 * entirely, so the server keeps its historical founding fonts (which is what
 * the classic wizard does when nothing was picked).
 *
 * The speaker step's "Same as captions" is NOT a third field here: while
 * `state.displaySame` is on, the reducer mirrors Body into Display, so this
 * function keeps reading exactly one value and cannot ship a stale face.
 */
export function buildFontRolesPayload(s: BrandSetupState): CreateChannelInput["fontRoles"] {
  const body = s.fonts.body.trim();
  const display = s.fonts.display.trim();
  if (!body && !display) return undefined;
  const b = body || display;
  const d = display || body;
  // `punch` means two different things per layout. In the founding RTL punch
  // layout it is the big bottom line, so it follows Display. In the Latin
  // `uniform` layout there is no punch line — the role only paints DIGITS
  // inside body text, so it follows Body (the same fan-out the study path
  // produces in `mapToPatch`). Setting a serif Display face there would put
  // one serif numeral in the middle of a sans sentence.
  const punch = s.language.direction === "rtl" ? d : b;
  return { caption: b, punch, speaker: b, speakerName: d };
}

/** Arabic → no language field (the founding pipeline, byte-identical); else
 *  the channel carries it. Same rule as `Onboard.tsx buildLanguagePayload`. */
export function buildLanguagePayload(s: BrandSetupState): CreateChannelInput["language"] {
  const l = s.language;
  return l.code === "ar"
    ? undefined
    : {
        code: l.code,
        displayName: l.displayName,
        direction: l.direction,
        register: l.register,
        budgets: l.budgets,
        glyphRatio: l.glyphRatio,
      };
}

/** The whole create body. Optional sections are OMITTED, never nulled. */
export function buildBrandSetupPayload(s: BrandSetupState): CreateChannelInput {
  const fontRoles = buildFontRolesPayload(s);
  const language = buildLanguagePayload(s);
  const study = s.study;
  return {
    slug: effectiveSlug(s),
    displayName: s.brandName.trim(),
    brand: {
      accent: s.colors.accent,
      captionColor: s.colors.ink,
      captionAccent: s.colors.accent,
      speakerNameColor: s.colors.accent,
      paper: s.colors.paper,
      muted: s.colors.muted,
      logoDataUrl: s.logoDataUrl ?? "",
    },
    ...(s.fonts.uploaded ? { font: s.fonts.uploaded } : {}),
    ...(fontRoles ? { fontRoles } : {}),
    layout: buildLayoutPayload(s),
    features: { videos: true, posts: false, originals: false, autoScan: true },
    ...(language ? { language } : {}),
    ...(s.cueShape ? { cueShape: s.cueShape } : {}),
    ...(study ? { brandStudy: { id: study.id, at: study.updatedAt || study.createdAt } } : {}),
  };
}
